// ============================================================
// PlayoNews – Fetch-based API client for FastAPI backend
// ============================================================

const TOKEN_KEY = 'playonews_token'

/** Base URL prefix – empty because the API is served from the same origin at /api/* */
export const API_BASE = ''

// ---- Token helpers ----

export function getToken(): string | null {
  try {
    return localStorage.getItem(TOKEN_KEY)
  } catch {
    return null
  }
}

export function setToken(token: string): void {
  localStorage.setItem(TOKEN_KEY, token)
}

export function removeToken(): void {
  localStorage.removeItem(TOKEN_KEY)
}

// ---- API fetch wrapper ----

export interface ApiFetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown
}

/**
 * Thin wrapper around `fetch()` that:
 * 1. Prepends `/api` to the path
 * 2. Attaches `Authorization: Bearer <token>` when a token exists
 * 3. Sets `Content-Type: application/json` for POST / PUT / PATCH
 * 4. Throws on non-2xx responses (includes server error message when available)
 * 5. Returns the parsed JSON body
 */
export async function apiFetch<T = unknown>(
  path: string,
  options: ApiFetchOptions = {},
): Promise<T> {
  const { body, headers: extraHeaders, ...rest } = options

  const headers: Record<string, string> = {
    ...(extraHeaders as Record<string, string>),
  }

  // Auth header
  const token = getToken()
  if (token) {
    headers['Authorization'] = `Bearer ${token}`
  }

  // JSON content-type for mutating methods
  const method = (rest.method ?? 'GET').toUpperCase()
  if (['POST', 'PUT', 'PATCH'].includes(method) && body !== undefined) {
    headers['Content-Type'] = 'application/json'
  }

  const url = `${API_BASE}/api${path}`

  const response = await fetch(url, {
    ...rest,
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  })

  if (!response.ok) {
    let message = `API error ${response.status}`
    try {
      const errorBody = await response.json()
      message = errorBody?.detail ?? errorBody?.message ?? errorBody?.error ?? message
    } catch {
      // Response body wasn't JSON – keep the default message
    }
    throw new Error(message)
  }

  // 204 No Content – nothing to parse
  if (response.status === 204) {
    return undefined as unknown as T
  }

  return response.json() as Promise<T>
}
